import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertTriangle, ArrowRight, Check, ShieldAlert, Stethoscope, X, Mail } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import useModalA11y from "@/lib/useModalA11y";

const CONSULT = { path: "/contact", label: "Request an initial consultation" };

export function WaitlistForm({ pathway, responseId, compact }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [consent, setConsent] = useState(false);
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return toast.error("Please enter a valid email");
    if (!consent) return toast.error("Please tick the consent box");
    setBusy(true);
    try {
      const r = await api.post("/ankle-finder/waitlist", { email, name, consent, pathway_interest: pathway || "GENERAL", response_id: responseId });
      setDone(r.data.status);
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not join the waiting list"); }
    finally { setBusy(false); }
  };
  if (done) {
    return <div className="text-sm text-emerald-300 flex items-center gap-2" data-testid="waitlist-done"><Check size={14} /> {done === "already_subscribed" ? "You're already on the list — we've noted your interest." : "You're on the list. We'll email you at launch."}</div>;
  }
  return (
    <form onSubmit={submit} className={`space-y-2 ${compact ? "" : "mt-3"}`} data-testid="waitlist-form">
      <div className="grid sm:grid-cols-2 gap-2">
        <input className="fl-input text-sm" placeholder="Name (optional)" value={name} onChange={(e) => setName(e.target.value)} data-testid="waitlist-name" />
        <input className="fl-input text-sm" type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="waitlist-email" />
      </div>
      <label className="flex items-start gap-2 text-xs text-zinc-400 cursor-pointer">
        <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5" data-testid="waitlist-consent" />
        <span>I agree to receive launch updates and offers for Functional LAB self-guided ankle programmes. No other marketing. Unsubscribe any time. <Link to="/privacy" className="text-[#f47a20] hover:underline">Privacy</Link></span>
      </label>
      <button type="submit" disabled={busy} className="fl-btn text-sm" data-testid="waitlist-submit"><Mail size={14} /> Join the waiting list</button>
    </form>
  );
}

function NextStepModal({ open, onClose, pathway, responseId, settings }) {
  const ref = useModalA11y({ open, onClose });
  const [showWait, setShowWait] = useState(false);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-black/70 p-4" role="dialog" aria-modal="true" aria-labelledby="next-step-title" ref={ref} data-testid="next-step-modal">
      <div className="fl-card w-full max-w-lg p-6 relative">
        <button onClick={onClose} className="absolute top-3 right-3 text-zinc-400 hover:text-white" aria-label="Close" data-testid="next-step-close"><X size={18} /></button>
        <div className="fl-label text-[#f47a20]">Choose your next step</div>
        <h2 id="next-step-title" className="fl-heading text-xl mt-1">Your pathway is in development</h2>
        <p className="text-sm text-zinc-300 mt-3">Our self-guided ankle programmes are in development. You can book an initial consultation to explore a personalised programme, or join the waiting list for launch updates.</p>
        {settings?.launch_target_date && <p className="text-xs text-zinc-400 mt-2">Target launch: {new Date(settings.launch_target_date).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</p>}
        {settings?.offer_text && <p className="text-xs text-[#f47a20] mt-1">{settings.offer_text}</p>}
        <div className="mt-5 flex flex-col sm:flex-row gap-2">
          <Link to={CONSULT.path} className="fl-btn text-sm justify-center" data-testid="next-step-consult"><Stethoscope size={14} /> Book an initial consultation</Link>
          <button type="button" onClick={() => setShowWait(true)} className="fl-btn-ghost text-sm justify-center" data-testid="next-step-waitlist">Join the waiting list</button>
        </div>
        {showWait && <WaitlistForm pathway={pathway} responseId={responseId} />}
      </div>
    </div>
  );
}

export function AnkleResult({ result, user, guest, onRestart }) {
  const navigate = useNavigate();
  const [modal, setModal] = useState(false);
  const shown = useRef(false);
  const unavailableMatch = result.outcome === "PATHWAY_MATCH" && result.availability !== "available";
  useEffect(() => { if (unavailableMatch && !shown.current) { shown.current = true; setModal(true); } }, [unavailableMatch]);

  const proceed = () => {
    if (!result.token) return;
    if (user) {
      navigate(`/checkout/summary?${new URLSearchParams({ package: result.programme.id, plan: "full", screen: result.token })}`);
    } else {
      sessionStorage.setItem("ankle_pending_claim", JSON.stringify({ response_id: result.response_id, token: result.token, package: result.programme.id }));
      navigate(`/register?redirect=${encodeURIComponent("/online-programmes/ankle/finder?claim=1")}`);
    }
  };

  const tone = { URGENT_MEDICAL: "red", MEDICAL_REVIEW: "amber", ASSESSMENT_REQUIRED: "amber", PATHWAY_MATCH: "green" }[result.outcome];
  const colours = { red: "border-red-500/50 text-red-300", amber: "border-amber-500/50 text-amber-300", green: "border-emerald-500/50 text-emerald-300" }[tone];
  const Icon = tone === "red" ? ShieldAlert : tone === "amber" ? AlertTriangle : Check;
  const copy = result.copy || {};

  return (
    <div className="space-y-4" data-testid="ankle-result" data-outcome={result.outcome} data-availability={result.availability}>
      <div className={`fl-card p-6 border ${colours}`}>
        <div className="flex items-start gap-3">
          <Icon size={24} />
          <div className="flex-1">
            <div className="fl-label opacity-80">Your result</div>
            {result.outcome === "PATHWAY_MATCH" ? (
              <>
                <h2 className="fl-heading text-2xl mt-1 text-white" data-testid="result-title">Your Programme Match</h2>
                <p className="text-sm text-zinc-200 mt-2">Your answers most closely match the <strong>Functional LAB {result.pathway_label}</strong> pathway.</p>
                <p className="text-xs text-zinc-400 mt-1">This is a programme match, not a medical diagnosis.</p>
              </>
            ) : (
              <>
                <h2 className="fl-heading text-2xl mt-1 text-white" data-testid="result-title">{copy.title}</h2>
                <p className="text-sm text-zinc-200 mt-2">{copy.body}</p>
              </>
            )}
            {result.outcome === "URGENT_MEDICAL" && <p className="text-sm mt-3 text-red-200">If you have chest pain, breathlessness, a visible deformity or an open injury, call <strong>999</strong> or go to A&E now. For other urgent concerns call <strong>NHS 111</strong>.</p>}
            {result.outcome === "MEDICAL_REVIEW" && <p className="text-sm mt-3 text-amber-200">Contact your GP, NHS 111 or a minor-injuries unit for an assessment. Rest, ice and avoid loading the ankle until you have been seen.</p>}
          </div>
        </div>
      </div>

      {result.outcome === "PATHWAY_MATCH" && result.availability === "available" && (
        <div className="fl-card p-6" data-testid="result-available">
          <div className="fl-label text-[#f47a20]">Programme available</div>
          <div className="fl-heading text-xl mt-1">{result.programme.name}</div>
          <div className="text-sm text-zinc-400 mt-1">{result.programme.headline} · £{Number(result.programme.price).toFixed(0)} · {result.programme.delivery_type === "interactive_plan" ? "Interactive My Plan programme" : "PDF programme"}</div>
          <div className="mt-4 flex flex-col sm:flex-row gap-2">
            <button onClick={proceed} className="fl-btn text-sm justify-center" data-testid="result-continue-checkout">{user ? "Continue to checkout" : "Create account or log in to continue"} <ArrowRight size={14} /></button>
            <Link to={CONSULT.path} className="fl-btn-ghost text-sm justify-center" data-testid="result-consult">{CONSULT.label}</Link>
          </div>
        </div>
      )}

      {unavailableMatch && (
        <div className="fl-card p-6" data-testid="result-unavailable">
          <div className="fl-label text-[#f47a20]">Choose your next step</div>
          <p className="text-sm text-zinc-300 mt-2">Our self-guided ankle programmes are in development. You can book an initial consultation to explore a personalised programme, or join the waiting list for launch updates.</p>
          {result.settings?.launch_target_date && <p className="text-xs text-zinc-400 mt-2">Target launch: {new Date(result.settings.launch_target_date).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</p>}
          {result.settings?.offer_text && <p className="text-xs text-[#f47a20] mt-1">{result.settings.offer_text}</p>}
          <div className="mt-4 flex flex-col sm:flex-row gap-2">
            <Link to={CONSULT.path} className="fl-btn text-sm justify-center" data-testid="result-consult">{CONSULT.label}</Link>
          </div>
          <div className="mt-5 pt-4 border-t border-white/10">
            <div className="text-sm font-medium">Join the waiting list</div>
            <WaitlistForm pathway={result.pathway} responseId={result.response_id} />
          </div>
        </div>
      )}

      {(result.outcome === "ASSESSMENT_REQUIRED" || result.outcome === "MEDICAL_REVIEW") && (
        <div className="fl-card p-6" data-testid="result-assessment-cta">
          <p className="text-sm text-zinc-300">Once any urgent concerns have been addressed, a Functional LAB clinician can assess you and plan the right next step.</p>
          <div className="mt-3 flex flex-col sm:flex-row gap-2">
            <Link to={CONSULT.path} className="fl-btn text-sm justify-center" data-testid="result-consult">{CONSULT.label}</Link>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between text-xs text-zinc-500">
        <span>Questionnaire {result.version} · reference {result.response_id?.slice(0, 8)}</span>
        <button type="button" onClick={onRestart} className="text-[#f47a20] hover:underline" data-testid="result-restart">Start again</button>
      </div>
      <NextStepModal open={modal} onClose={() => setModal(false)} pathway={result.pathway} responseId={result.response_id} settings={result.settings} />
    </div>
  );
}
